import { Avatar, Button, Tooltip } from 'flowbite-react';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { Img } from 'react-image';
import Lottie from 'react-lottie-player';
import { generateValidity } from 'src/utils/helper';
import { ApplicationStatus, PositionStatus, UIPosition, UIUser, UIUserRecommendation, UserRole } from 'types';
import arrowAnimation from '../../shared/animationFiles/arrowAnimation.json';
import { FaLock } from 'react-icons/fa6';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { useToast } from 'src/context/ToastProvider';
import { generateCompanyLogoAvatarSrc } from 'src/utils/companyLogo';
import { createApplication } from 'src/apis/application';
import { get } from 'lodash';
import { Loader } from 'ui';
import { IoShareSocialSharp } from 'react-icons/io5';
import useDisclosure from 'src/hooks/utils/useDisclosure';
import ShareablePositionModal from '../postionDrawer/ShareablePositionModal';
import MisMatchModal from './MisMatchModal';
import LinkedInImport from 'src/components/profile/LinkedInImport';
import { allowAllUsers } from 'src/config/env.config';
import { isUserAllowedToApply } from 'shared-utils';

interface Props {
	positionTitle: string;
	companyName: string;
	postedOn: Date;
	positionLevel: number;
	companyLogo: string;
	images?: string[];
	positionDetail: UIPosition;
	numberOfSkillsBelowThreshold: number;
	isUserAlreadyApplied: boolean | null;
	userDetail: UIUser;
	positionRecommendations?: UIUserRecommendation[];
	shouldBeUnlocked?: boolean;
	subTextForReason?: string;
}

const JobHeader = ({
	positionDetail,
	postedOn,
	positionTitle,
	companyLogo,
	numberOfSkillsBelowThreshold,
	isUserAlreadyApplied,
	userDetail,
	shouldBeUnlocked,
	positionRecommendations,
	subTextForReason,
}: Props) => {
	const navigate = useNavigate();
	const currentTime = moment();
	const targetPostedOnTime = moment(postedOn).format();
	const postedOnDiff = moment.duration(currentTime.diff(targetPostedOnTime));
	const queryClient = useQueryClient();
	const toast = useToast();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [isUnlocked, setIsUnlocked] = useState<boolean>();
	const [avatarImg, setAvatarImg] = useState<string>('');

	const { isOpen: isOpenMismatch, onOpen: onOpenMismatch, onClose: onCloseMismatch } = useDisclosure();
	const { isOpen: isLinkedInModalOpen, onOpen: onOpenLinkedInModal, onClose: onCloseLinkedInModal } = useDisclosure();

	const generateAvatar = useCallback(async () => {
		const res = await generateCompanyLogoAvatarSrc(positionDetail?.company?.logo || '', positionDetail?.company?.url);
		setAvatarImg(res);
	}, [positionDetail?.company?.logo, positionDetail?.company?.url]);

	const createApplicationFn = useMutation({
		mutationKey: ['createApplication'],
		mutationFn: createApplication,
		onSuccess() {
			queryClient.refetchQueries(['getPositions']);
			queryClient.refetchQueries(['getPositionDetailsByIdQuery']);
			queryClient.refetchQueries(['GetApplicationsCount']);
			toast({
				title: 'Succesfully applied!',
				type: 'success',
			});
			onCloseLinkedInModal();
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				title: errorMessage,
				type: 'error',
			});
			onCloseLinkedInModal();
		},
	});

	useEffect(() => {
		generateAvatar();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [positionDetail?.company?.logo, positionDetail?.company?.url]);

	// Checking that wheather the user is eligible for job or not.
	useEffect(() => {
		positionRecommendations?.find(recommendation => {
			console.log('started:');
			if (recommendation?.user?._id === userDetail._id) console.log('recommendation:', recommendation);
			return recommendation?.user?._id === userDetail._id && recommendation?.isEligible;
		});
		if (isUserAlreadyApplied) {
			setIsUnlocked(false);
		} else if (
			positionRecommendations?.find(
				recommendation => recommendation?.user?._id === userDetail._id && recommendation?.isEligible,
			)
		) {
			setIsUnlocked(true);
		} else if (shouldBeUnlocked) {
			setIsUnlocked(true);
		} else {
			setIsUnlocked(false);
		}

		if (allowAllUsers && !isUserAlreadyApplied) {
			setIsUnlocked(true);
		} else if (isUserAlreadyApplied) {
			setIsUnlocked(false);
		}
		// if user is placed then user can't apply for the job
		if (
			!isUserAllowedToApply({
				isUserPlaced: !!userDetail?.flags?.isPlaced,
			})
		) {
			setIsUnlocked(false);
		}
	}, [
		userDetail?.evaluation?.level,
		isUserAlreadyApplied,
		shouldBeUnlocked,
		positionRecommendations,
		positionDetail.positionRequirement?.levelThreshold,
		userDetail,
		positionDetail.positionRequirement.skillThresholds,
		positionDetail._id,
	]);

	const proceedToApply = () => {
		if (
			// typeof userDetail.evaluation?.level === 'number' &&
			// userDetail.evaluation?.level >= positionDetail.positionRequirement.levelThreshold &&
			// userDetail.profile.totalExperience >= positionDetail.positionRequirement.workExperience.min
			isUnlocked
		) {
			createApplicationFn.mutate({
				id: positionDetail._id,
				user: String(userDetail?._id),
				status: ApplicationStatus.APPLIED_BY_JOBSEEKER,
			});
		} else {
			onCloseLinkedInModal();
			onOpenMismatch();
		}
	};

	useEffect(() => {
		console.log({ isUnlocked });
	}, [isUnlocked]);

	// Function for creating new Application when click on Apply Button.
	const onApply = () => {
		if (
			// typeof userDetail.evaluation?.level === 'number' &&
			// userDetail.evaluation?.level >= positionDetail.positionRequirement.levelThreshold &&
			// userDetail.profile.totalExperience >= positionDetail.positionRequirement.workExperience.min
			isUnlocked
		) {
			if (!userDetail.profile.linkedInProfile) onOpenLinkedInModal();
			else proceedToApply();
		} else {
			onOpenMismatch();
		}
	};

	// const positionTitle = generatePositionTitle(positionDetail.title as PositionTitle);

	return (
		<div className=" w-full flex justify-between sm:flex-col">
			<div className="flex flex-col gap-3">
				<div className=" flex gap-4">
					<div className="pt-1">
						<Avatar
							className="job-header-company-image"
							size={'md'}
							alt="companyLogo"
							img={companyLogo}
							rounded={true}
							bordered
						/>
					</div>
					{/* title and companyName */}
					<div className=" flex flex-col gap-1">
						<div className="flex gap-x-2  items-center">
							<div className="text-xl font-bold flex gap-3 items-center justify-between">
								{positionTitle.length > 30 ? (
									<Tooltip content={positionTitle} placement="top">
										{positionTitle.substring(0, 27)}...
									</Tooltip>
								) : (
									<>{positionTitle}</>
								)}
							</div>
							<IoShareSocialSharp
								size={18}
								className="cursor-pointer text-[#6698FA] hover:text-[#4b6fb6] md:hidden"
								onClick={() => {
									onOpen();
								}}
							/>
						</div>

						<div
							onClick={() => window.open(`${positionDetail.company.url}`, '_blank')}
							className="mt-[4px] flex cursor-pointer items-center gap-2 text-base font-medium"
						>
							<span className="hover:underline">
								{positionDetail.company.name.length > 20 ? (
									<Tooltip content={positionDetail.company.name} placement="top">
										{positionDetail.company.name.length > 20
											? `${positionDetail.company.name.substring(0, 19)} ...`
											: `${positionDetail.company.name}`}
									</Tooltip>
								) : (
									<>
										{positionDetail.company.name.length > 20
											? `${positionDetail.company.name.substring(0, 19)} ...`
											: `${positionDetail.company.name}`}
									</>
								)}
							</span>
							<span>
								<Img
									loading="lazy"
									className=""
									src="/assets/externalLinkIcon.svg"
									alt="externalLinkIcon"
									width="16"
									height="16"
								/>
							</span>
						</div>

						{positionDetail?.hasBond ? (
							<div className="mt-2 bg-[#FFF8E5] rounded-[6px] px-2 py-1 hidden md:block max-h-[25px] max-w-max text-[#E57A00] dark:text-[#FACA15] text-xs font-[500] font-inter dark:bg-[#374151] dark:border dark:border-[#FACA15]">
								*This job includes bond agreement.
							</div>
						) : null}
					</div>
				</div>
				{/* validity */}
				<div className="dark:text-[#A9EFF2] text-[#7777E7] text-[14px] col-span-5 flex items-center">
					<p>Posted {generateValidity(postedOnDiff, 'postedOn')}</p>
				</div>
			</div>
			{/* Right Side */}
			<div className="">
				{!userDetail || (userDetail && userDetail.role === UserRole.MENTOR) ? (
					<div className=" flex gap-4 justify-center items-center ml-3 sm:ml-0 mr-4 sm:justify-between">
						<Lottie loop animationData={arrowAnimation} play={true} className="w-[73px] mr-3 sm:hidden" />
						<div>
							Reach
							<span className="text-[#39D98A]">{' Level '}</span>
							<span>
								<Img
									className="mb-1 inline-block"
									src="/assets/lightning-bolt.png"
									alt="lightningBolt"
									width={16}
									height={16}
								/>{' '}
							</span>
							{Math.round(positionDetail.positionRequirement.levelThreshold)}+ to apply{' '}
						</div>
						<div className={userDetail && userDetail.role === UserRole.MENTOR ? 'hidden' : 'ml-[26px] sm:ml-0'}>
							<Button
								color="purplePlaced"
								size={'md'}
								onClick={() => navigate(`/auth/register?callback=${encodeURIComponent(`${window.location.href}`)}`)}
							>
								Apply
							</Button>
						</div>
					</div>
				) : (
					<div className={'grow justify-between flex items-center sm:w-full sm:justify-between'}>
						<Lottie loop animationData={arrowAnimation} play={true} className="w-[73px] mr-3 sm:hidden" />

						{positionDetail.positionStatus === PositionStatus.CLOSED ? (
							<>
								{isUserAlreadyApplied ? (
									<div className={'ml-3 sm:ml-0 mr-4'}>
										<div className="text-base font-medium">{`🎉 Congratulations on applying.`}</div>
										<div className="mt-[4px] text-xs font-normal text-[#00B7C4] dark:text-[#73DFE7]">
											Stay tuned for updates on the next steps.
										</div>
									</div>
								) : (
									<div className={'ml-3 sm:ml-0 mr-4'}>
										<div className="text-[16px]">
											<span className="dark:text-white">{`Hired `}</span>
											<span className="text-[#5B8DEF]">{positionDetail.hired ? positionDetail.hired : ''}</span>
											<span className="dark:text-white">{' aspirants at '}</span>
											<span className="font-[500] text-[#06C270] dark:text-[#39D98A]">{'Level '}</span>
											<span>
												<Img
													className="inline-block items-center"
													src="/assets/lightning-bolt.png"
													alt="lightningBolt"
													width={11}
													height={14}
												/>{' '}
											</span>
											<span className="dark:text-white">{`${Math.round(
												positionDetail.positionRequirement.levelThreshold,
											)}`}</span>
										</div>
										<div className="mt-[4px] text-xs font-normal text-[#00B7C4] dark:text-[#73DFE7]">
											Stay connected with us for future job opportunities.
										</div>
									</div>
								)}
							</>
						) : (
							<>
								{isUnlocked ? (
									<div
										className={'ml-3 sm:ml-0 mr-4 text-base font-medium '}
									>{`🎉 Hurray! You're eligible to apply.`}</div>
								) : isUserAlreadyApplied ? (
									<>
										<div className={`${'ml-3 sm:ml-0 mr-4'}`}>
											<div className="text-base font-medium">{`🎉 Congratulations on applying.`}</div>
											<div className="mt-[4px] text-xs font-normal text-[#00B7C4] dark:text-[#73DFE7]">
												Stay tuned for updates on the next steps.
											</div>
										</div>
									</>
								) : (
									<div className={`${'ml-3 sm:ml-0 mr-4'} text-base font-medium`}>
										<>
											<div className="">Company requirement mismatch</div>
											<div className="mt-[4px] text-xs font-normal dark:text-[#73DFE7]">
												{subTextForReason || 'You are not eligible for this position'}
											</div>
										</>
									</div>
								)}
							</>
						)}

						{positionDetail.positionStatus === PositionStatus.CLOSED ? (
							<>
								{isUserAlreadyApplied ? (
									<div className="ml-[26px] sm:ml-0">
										<Button color="purplePlaced" style={{ backgroundColor: '#05A660' }} size={'md'} disabled>
											Applied
										</Button>
									</div>
								) : (
									<div className="ml-[26px] sm:ml-0">
										<Button color="purplePlaced" size={'md'} disabled>
											Expired
										</Button>
									</div>
								)}
							</>
						) : (
							<>
								{isUserAlreadyApplied ? (
									<div className="ml-[26px] sm:ml-0">
										<Button color="purplePlaced" style={{ backgroundColor: '#05A660' }} size={'md'} disabled>
											Applied
										</Button>
									</div>
								) : isUnlocked ? (
									<div className="ml-[26px] sm:ml-0">
										<Button color="purplePlaced" size="md" onClick={onApply} disabled={createApplicationFn.isLoading}>
											{createApplicationFn.isLoading ? <Loader /> : `Apply`}
										</Button>
									</div>
								) : (
									<>
										<div className="ml-[26px] sm:ml-0">
											<Button color="purplePlaced" size="md" disabled>
												<FaLock className="mr-2" />
												Apply
											</Button>
										</div>
									</>
								)}
								<div
									className="border sm:hidden md:flex items-center justify-center p-2 cursor-pointer hover:text-blue-700 hover:border-blue-700 ml-4 rounded-[8px] border-[#6698FA] text-[#6698FA]"
									onClick={() => {
										onOpen();
									}}
								>
									<IoShareSocialSharp size={20} />
								</div>
							</>
						)}
					</div>
				)}
				<ShareablePositionModal
					avatarImg={avatarImg}
					isOpen={isOpen}
					onClose={onClose}
					positionDetail={positionDetail as UIPosition}
				/>
				<MisMatchModal
					isOpen={isOpenMismatch}
					onClose={onCloseMismatch}
					positionTitle={positionTitle}
					companyName={positionDetail.company.name}
					workexp={positionDetail.positionRequirement.workExperience.min}
				/>
				<LinkedInImport isOpen={isLinkedInModalOpen} onClose={onCloseLinkedInModal} onProceed={proceedToApply} />
			</div>
		</div>
	);
};

export default JobHeader;
